import React, { useRef } from 'react';
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { DURATION, EASE_OUT, STAGGER, VIEWPORT } from './variants';

type Mode = 'scroll' | 'enter';
type Tag = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';

type Props = {
  /** Plain text. Use `\n` to force a line break. */
  children: string;
  as?: Tag;
  className?: string;
  id?: string;
  /**
   * `scroll` (default) — words brighten one-by-one tied to scroll position.
   * `enter` — one-shot word stagger when the element enters view (for
   * above-the-fold headings that a scroll-linked reveal would never reach).
   */
  mode?: Mode;
  /** Word indices that should receive the accent class. */
  accentWords?: number[];
  accentClassName?: string;
  /** Class applied to every non-accent word. */
  wordClassName?: string;
};

type Token = { word: string; index: number } | { br: true };

/** Split text into word/line-break tokens, indexing words only. */
const tokenize = (text: string): Token[] => {
  const tokens: Token[] = [];
  let index = 0;
  text.split('\n').forEach((line, lineIdx) => {
    if (lineIdx > 0) tokens.push({ br: true });
    line
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => {
        tokens.push({ word, index: index++ });
      });
  });
  return tokens;
};

const MOTION_TAGS = {
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  h4: motion.h4,
  p: motion.p,
  span: motion.span,
};

/* ----- scroll mode: one word, opacity tied to a slice of scroll ----- */
const ScrollWord: React.FC<{
  progress: MotionValue<number>;
  range: [number, number];
  className?: string;
  children: string;
}> = ({ progress, range, className, children }) => {
  const opacity = useTransform(progress, range, [0.14, 1]);
  const y = useTransform(progress, range, [8, 0]);
  return (
    <motion.span
      className={className}
      style={{ opacity, y, display: 'inline-block', willChange: 'opacity, transform' }}
    >
      {children}
    </motion.span>
  );
};

/* ----- enter mode: per-word fade + rise variant ----- */
const enterWordVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.text, ease: EASE_OUT },
  },
};

/**
 * Heading that reveals word-by-word. In `scroll` mode each word's
 * opacity is bound to scroll position — the signature 2026 effect.
 */
const ScrollText: React.FC<Props> = ({
  children,
  as = 'h2',
  className,
  id,
  mode = 'scroll',
  accentWords = [],
  accentClassName,
  wordClassName,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef<HTMLElement | null>(null);
  // Progress 0 when the heading top hits 85% of the viewport, 1 at 35%.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.85', 'start 0.35'],
  });

  const tokens = tokenize(children);
  const wordCount = Math.max(
    tokens.filter((t): t is { word: string; index: number } => 'word' in t).length,
    1,
  );
  const accent = new Set(accentWords);
  const accentClassFor = (i: number) =>
    accent.has(i) ? accentClassName : wordClassName;

  /* Reduced motion — render a plain heading with accent spans intact. */
  if (prefersReducedMotion) {
    return React.createElement(
      as,
      { className, id },
      tokens.map((t, i) =>
        'br' in t ? (
          <br key={i} />
        ) : (
          <React.Fragment key={i}>
            <span className={accentClassFor(t.index)}>{t.word}</span>{' '}
          </React.Fragment>
        ),
      ),
    );
  }

  /* Enter mode — one-shot staggered word reveal. */
  if (mode === 'enter') {
    const MotionTag = MOTION_TAGS[as] as React.ElementType;
    return (
      <MotionTag
        className={className}
        id={id}
        initial="hidden"
        whileInView="visible"
        viewport={VIEWPORT}
        transition={{ staggerChildren: STAGGER }}
      >
        {tokens.map((t, i) =>
          'br' in t ? (
            <br key={i} />
          ) : (
            <React.Fragment key={i}>
              <motion.span
                className={accentClassFor(t.index)}
                style={{ display: 'inline-block', willChange: 'opacity, transform' }}
                variants={enterWordVariants}
              >
                {t.word}
              </motion.span>{' '}
            </React.Fragment>
          ),
        )}
      </MotionTag>
    );
  }

  /* Scroll mode — words brighten with scroll progress. */
  return React.createElement(
    as as React.ElementType,
    { className, id, ref },
    tokens.map((t, i) => {
      if ('br' in t) return <br key={i} />;
      const start = t.index / wordCount;
      const end = (t.index + 1) / wordCount;
      return (
        <React.Fragment key={i}>
          <ScrollWord
            progress={scrollYProgress}
            range={[start, end]}
            className={accentClassFor(t.index)}
          >
            {t.word}
          </ScrollWord>{' '}
        </React.Fragment>
      );
    }),
  );
};

export default ScrollText;
